import React, { ChangeEvent, useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  styled,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Contexts und Interfaces
import { TextSizeContext } from "data/index";
import { ChangePasswordInterface, SecurityTabPropType } from "entities/interfaces";
import WithSecurityTabData from "ui/containers/settings/SecurityTabContainer";

// Shared Components
import TextBox from "ui/components/shared/TextBox";
import { SubmitButton } from "ui/components/shared/SubmitButton";
import ConfirmationModal from "ui/components/shared/ConfirmationModal";

// === Mobile Header Component ===
const MobileHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '0.75rem 1rem',
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '2rem',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'sticky',
  top: 0,
  zIndex: 100,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  flex: 1,
}));

// === Mobile Container ===
const MobileContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  paddingBottom: '2rem',
}));

// === Content Wrapper ===
const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: '1rem',
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

// === Mobile Cards ===
const MobileCard = styled(Paper)(({ theme }) => ({
  padding: "1.5rem",
  borderRadius: "12px",
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
  backgroundColor: theme.palette.common.white,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.125rem",
  color: theme.palette.text.primary,
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const FormSection = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

// === Component ===
const MobileSecuritySettings = ({ updatePassword, deleteAccount }: SecurityTabPropType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: textSize } = useContext(TextSizeContext);

  const [formData, setFormData] = useState<ChangePasswordInterface>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState<ChangePasswordInterface>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [saveDisabled, setSaveDisabled] = useState(true);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);

  const handleFormChange = (
    event: ChangeEvent<HTMLInputElement>,
    key: keyof ChangePasswordInterface
  ) => {
    setFormData({ ...formData, [key]: event.target.value });
    setError({ ...error, [key]: "" });
    setSaveDisabled(false);
  };

  const handleSave = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    if (!oldPassword || !newPassword) {
      setError({
        ...error,
        oldPassword: !oldPassword ? `${t("Current Password")} ${t("required")}` : "",
        newPassword: !newPassword ? `${t("New Password")} ${t("required")}` : "",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setError({
        ...error,
        confirmPassword: t("Password didn't match!"),
      });
      return;
    }

    const resp = await updatePassword(formData);
    if (resp.status === 200) {
      setSaveDisabled(true);
      
      // Optional: Success feedback
      console.log("Password updated successfully");
    }
  };

  const handleDelete = async () => {
    const resp = await deleteAccount();
    if (resp.status === 200) {
      localStorage.removeItem("currentUserName");
      localStorage.removeItem("currentUserToken");
      navigate("/auth");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MobileContainer>
      {/* Mobile Header */}
      <MobileHeader>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <HeaderTitle>{t("Security")}</HeaderTitle>
      </MobileHeader>

      {/* Content */}
      <ContentWrapper>
        {/* Passwort ändern */}
        <MobileCard>
          <SectionTitle>{t("Change Password")}</SectionTitle>
          <SectionDescription>
            {t("Settings and recommendations to help you keep your account secure")}
          </SectionDescription>

          <Divider />

          <FormSection>
            <TextBox
              label={t("Current Password")}
              value={formData.oldPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange(e, "oldPassword")}
              type="password"
              error={error.oldPassword}
              isRequired
            />
            
            <TextBox
              label={t("New Password")}
              value={formData.newPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange(e, "newPassword")}
              type="password"
              error={error.newPassword}
              isRequired
            />
            
            <TextBox
              label={t("Confirm Password")}
              value={formData.confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange(e, "confirmPassword")}
              type="password"
              error={error.confirmPassword}
              isRequired
            />

            <SubmitButton
              color="success"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                padding: '0.75rem 1.5rem',
                marginTop: '0.5rem',
              }}
            >
              {t("Save")}
            </SubmitButton>
          </FormSection>
        </MobileCard>

        {/* Konto löschen */}
        <MobileCard>
          <SectionTitle>{t("Delete Account")}</SectionTitle>
          <SectionDescription>
            {t("Deleting your account will remove all of your information from out database. This cannot be undone.")}
          </SectionDescription>

          <SubmitButton
            color="error"
            onClick={() => setDeleteConfirmationModal(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              padding: '0.75rem 1.5rem',
              marginTop: '0.5rem',
            }}
          >
            {t("Delete Account")}
          </SubmitButton>
        </MobileCard>
      </ContentWrapper>

      {/* Delete Confirmation Modal */}
      {deleteConfirmationModal && (
        <ConfirmationModal
          closeModal={() => setDeleteConfirmationModal(false)}
          confirmColor="error"
          handleConfirm={handleDelete}
          title={t("Deleting Account")}
          description={t("Are you sure you want to delete this account?")}
        />
      )}
    </MobileContainer>
  );
};

export default WithSecurityTabData(MobileSecuritySettings);